/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * HighlightBox.tsx — Highligh\1 \2ox (services/operator-web-app).
 */

import type { ReactNode } from 'react';
import { useHighlight } from './useHighlight';
import type { HighlightWidget } from '@/store/slices/copilotSlice';
import { featureFlags } from '@/config/featureFlags';
import { useTranslation } from '@/i18n';

const LEVEL_CLASSES: Record<'soft' | 'medium' | 'critical', string> = {
  soft: 'ring-2 ring-blue-400 dark:ring-blue-500 ring-offset-2 dark:ring-offset-slate-900 animate-[pulse_2s_ease-in-out_infinite]',
  medium:
    'ring-4 ring-sky-500 dark:ring-sky-400 ring-offset-2 dark:ring-offset-slate-900 shadow-lg shadow-sky-500/20',
  critical:
    'ring-4 ring-amber-500 dark:ring-amber-400 ring-offset-2 dark:ring-offset-slate-900 animate-[pulse_1.5s_ease-in-out_infinite]',
};

export interface HighlightBoxProps {
  widgetId: HighlightWidget;
  children: ReactNode;
  className?: string;
  /** When true, clear highlight on click/focus (operator action). */
  clearOnInteract?: boolean;
}

/**
 * E6-S3: Wrapper that applies soft highlight ring when copilot recommends this widget.
 * E8-S1: Highlight effect gated by featureFlags.aiHighlights (rollback-safe).
 * No scroll or forced navigation. Plan: 09.
 */
export function HighlightBox({
  widgetId,
  children,
  className = '',
  clearOnInteract = true,
}: HighlightBoxProps) {
  const t = useTranslation();
  const { isHighlighted, reason, level, clearHighlight } = useHighlight(widgetId);
  const highlightsEnabled = featureFlags.aiHighlights;
  const ringClass = highlightsEnabled && isHighlighted && level ? LEVEL_CLASSES[level] : '';

  const handleInteraction = () => {
    if (clearOnInteract && isHighlighted) clearHighlight();
  };

  return (
    <div
      className={`rounded-lg transition-[box-shadow,ring] ${ringClass} ${className}`}
      onFocusCapture={handleInteraction}
      onClickCapture={handleInteraction}
      role={isHighlighted ? 'region' : undefined}
      aria-label={isHighlighted && reason ? t('highlight.reason', { reason }) : undefined}
    >
      {children}
    </div>
  );
}
